import { LEAD_SOURCES } from '../lib/lead-sources';
import { apiFetch } from '../auth/api';
/*
 * Migration 0182 — the future pipeline. Intent, confidence and the bucket
 * boundaries come from shared-types, so the columns this app draws and the
 * grouping the API does are the same definition. Re-exported here because every
 * consumer already imports from this module and should not have to know which
 * package a lead type happens to live in.
 */
import {
  LEAD_CONFIDENCES,
  LEAD_INTENTS,
  PIPELINE_BUCKETS,
  pipelineBucketFor,
  targetMonthLabel,
  targetForBucket,
  isLaterBucket,
  leadConfidenceLabel,
  leadIntentLabel,
  type LeadConfidence,
  type LeadIntent,
  type PipelineBucket,
} from '@transaction-rocket/shared-types';

export {
  LEAD_CONFIDENCES,
  LEAD_INTENTS,
  PIPELINE_BUCKETS,
  pipelineBucketFor,
  targetMonthLabel,
  targetForBucket,
  isLaterBucket,
  leadConfidenceLabel,
  leadIntentLabel,
};
export type { LeadConfidence, LeadIntent, PipelineBucket };

export type LeadStage = 'new' | 'nurturing' | 'active' | 'under_contract' | 'closed' | 'lost';

export const LEAD_STAGES: { value: LeadStage; label: string; color: string }[] = [
  { value: 'new', label: 'New', color: '#928DDD' },
  { value: 'nurturing', label: 'Nurturing', color: '#5BA0A8' },
  { value: 'active', label: 'Active', color: '#3F8E66' },
  { value: 'under_contract', label: 'Under contract', color: '#BF892B' },
  { value: 'closed', label: 'Closed', color: '#10B981' },
  { value: 'lost', label: 'Lost', color: '#9CA3AF' },
];

export type LeadType = 'auction' | 'listing' | 'buyer';

/** Migration 0146 (Daniel 2026-07-27) — traditional buyer vs investor.
 *  Null = not specified; the UI renders that as "Not set", never as
 *  "Traditional buyer". Carried onto the transaction on conversion. */
export type BuyerType = 'traditional' | 'investor';

export interface LeadListItem {
  id: number;
  contact_id: number;
  source: string | null;
  source_detail: string | null;
  stage: LeadStage;
  lead_type: LeadType | null;
  /** Migration 0146 — buyer leads only. Null = not specified. */
  buyer_type: BuyerType | null;
  notes: string;
  assigned_agent_id: number | null;
  lender_contact_id: number | null;
  score: number;
  last_activity_at: number | null;
  next_action_at: number | null;
  converted_transaction_id: number | null;
  created_at: number;
  updated_at: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  assignee_first: string | null;
  assignee_last: string | null;
  lender_first: string | null;
  lender_last: string | null;
  lender_company: string | null;
  lender_email: string | null;
  lender_phone: string | null;
  /** Build 11 (2026-09-14): the follow-up loop, on the list row. */
  lender_confirmed_at: number | null;
  follow_up_every_days: number | null;
  last_contacted_at: number | null;
  latest_loan_status: string | null;
  latest_loan_status_at: number | null;
  lender_checks_unanswered: number;
  lender_check_last_sent_at: number | null;
  // Round L-7 (Daniel 2026-06-09): pre-approval pipeline fields.
  // Null until the agent/lender fills them in.
  pre_approval_amount_cents: number | null;
  budget_min_cents: number | null;
  budget_max_cents: number | null;
  /**
   * Migration 0178 (Daniel 2026-08-04) — which property this lead came from.
   * A real transaction id, not a typed-in address, so it links and filters.
   * Null on manual leads, on leads from unmapped Meta forms, and on every row
   * that predates the column. Rendered as "Not set", never guessed.
   */
  property_transaction_id: number | null;
  /** Joined from the referenced transaction, so the address stays correct
   *  when the deal's address is edited. Null when no property is set. */
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  property_type: 'listing' | 'auction' | 'buyer' | null;
  /**
   * Migration 0182 — when this person expects to buy or sell.
   *
   * Month AND year, never a day: "around March 2027" is what a client actually
   * says, and a made-up day would read as an appointment. The pair moves
   * together — one without the other is treated as no timeline at all, both on
   * the server and on the board.
   */
  target_month: number | null;
  target_year: number | null;
  /** soft | likely | firm. Null until somebody asks. */
  target_confidence: LeadConfidence | null;
  /** buy | sell | both. What they are doing, not the form they arrived on. */
  intent: LeadIntent | null;
}

/** Build 71: why a lead died. Mirrors the CHECK in migration 0294. */
export type LostReason = 'another_agent' | 'not_ready' | 'unresponsive' | 'bad_contact' | 'price_financing' | 'not_a_lead' | 'other';
export const LOST_REASONS: Array<{ value: LostReason; label: string }> = [
  { value: 'another_agent', label: 'Went with another agent' },
  { value: 'not_ready', label: 'Not ready yet' },
  { value: 'unresponsive', label: 'Stopped responding' },
  { value: 'bad_contact', label: 'Bad number or email' },
  { value: 'price_financing', label: 'Price or financing' },
  { value: 'not_a_lead', label: 'Not a real lead' },
  { value: 'other', label: 'Other' },
];
export function lostReasonLabel(r: string | null | undefined): string {
  if (!r) return 'No reason recorded';
  return LOST_REASONS.find((x) => x.value === r)?.label ?? r;
}

export interface LeadDetail extends LeadListItem {
  /** Build 71. Set when stage is lost. */
  lost_reason?: LostReason | null;
  lost_note?: string | null;
  lost_at?: number | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  /** Routing claim state — null when not in a claim flow (Daniel 2026-06-04). */
  claim_state: 'unclaimed' | 'claimed' | null;
  claim_pending_user_id: number | null;
  claim_expires_at: number | null;
  claimed_at: number | null;
  claimed_by_user_id: number | null;
  external_source: string | null;
  external_id: string | null;
  // R-3 (Daniel 2026-08-04): Meta ad attribution, real columns since
  // migration 0166. Null for manual leads, pre-0166 rows, and tokens
  // without ads access — the UI hides them rather than showing dashes.
  ad_name: string | null;
  adset_name: string | null;
  campaign_name: string | null;
  form_name: string | null;
  /**
   * Migration 0179 — Meta's own error text explaining why `ad_name` is NULL.
   * NULL means nothing went wrong, which is true of every lead that has an ad
   * name and honest about older leads whose reason is genuinely unknown. The
   * card shows it verbatim: a permissions error and an organic lead look
   * identical from the outside, and only Meta's wording tells them apart.
   */
  attribution_error: string | null;
  /**
   * Migration 0179 — the name Daniel typed for the form, joined from
   * meta_lead_forms. Beats `form_name` everywhere it exists, because a label a
   * human wrote beats a name a machine generated.
   */
  form_label: string | null;
  /**
   * The Meta page the form belongs to, joined through meta_lead_forms. Not
   * displayed on its own: it is the prefix Meta staples onto every generated
   * form name, and humanizeMetaName() needs it to strip that prefix.
   */
  page_name: string | null;
  // Build 3 (2026-07-21): lender portal-access status. Joined on the lead
  // detail endpoint from the lender contact's linked portal user
  // (contacts.linked_user_id → users). lender_user_id null / login_count 0
  // ⇒ the lender has no portal access yet.
  lender_user_id: number | null;
  lender_last_login_at: number | null;
  lender_login_count: number;
  /** Migration 0178 — only the detail endpoint joins the zip. */
  property_zip: string | null;
}

export function claimLead(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/leads/${id}/claim`, { method: 'POST' });
}

// ─── Unified inbox messages (Round A) ──────────────────────────────
export interface LeadMessage {
  id: number;
  channel: 'email' | 'sms';
  direction: 'inbound' | 'outbound';
  body: string;
  subject: string | null;
  occurred_at: number;
  is_read: number;
  agent_user_id: number | null;
  agent_first: string | null;
  agent_last: string | null;
  contact_first: string | null;
  contact_last: string | null;
  // Round K (2026-06-06): engagement signals from Resend webhook.
  // Populated only for outbound emails linked to an email_sends row.
  open_count?: number | null;
  click_count?: number | null;
  opened_at?: number | null;
  clicked_at?: number | null;
}

export function listLeadMessages(leadId: number): Promise<{ messages: LeadMessage[] }> {
  return apiFetch(`/api/leads/${leadId}/messages`);
}

export function listContactMessages(contactId: number): Promise<{ messages: LeadMessage[] }> {
  return apiFetch(`/api/contacts/${contactId}/messages`);
}

export function listTransactionMessages(txId: number): Promise<{ messages: LeadMessage[] }> {
  return apiFetch(`/api/transactions/${txId}/messages`);
}

// ─── Funnel diagnostics (Round C) ────────────────────────────────────
export interface FunnelStage {
  stage: LeadStage;
  label: string;
  count: number;
  entered_30d: number;
  avg_days_in_stage: number;
}

export interface FunnelSource {
  source: string;
  total: number;
  converted: number;
  conversion_rate_pct: number;
  avg_days_to_convert: number | null;
}

export interface FunnelTotals {
  total_leads: number;
  total_converted: number;
  total_lost: number;
  new_last_30d: number;
  conversion_rate_pct: number;
}

export interface FunnelAgent {
  agent_user_id: number;
  name: string;
  total: number;
  converted: number;
  conversion_rate_pct: number;
  avg_days_to_convert: number | null;
}

export interface FunnelFirstContact {
  total: number;
  never_contacted: number;
  under_5min: number;
  under_1hr: number;
  under_24hr: number;
  over_24hr: number;
  avg_minutes: number | null;
}

export function getFunnelStats(): Promise<{
  stages: FunnelStage[];
  sources: FunnelSource[];
  totals: FunnelTotals;
  agents: FunnelAgent[];
  first_contact: FunnelFirstContact;
}> {
  return apiFetch('/api/leads/_meta/funnel-stats');
}

export interface LeadStageHistoryEntry {
  stage: LeadStage;
  entered_at: number;
  notes: string | null;
  by_first: string | null;
  by_last: string | null;
}

export interface ListLeadsFilters {
  stage?: LeadStage | '';
  assignee?: 'me' | 'all' | 'unassigned' | number;
  source?: string;
  q?: string;
  /** Round D filters (Daniel 2026-06-04). */
  tag_ids?: number[];
  lead_type?: LeadType | '';
  /** "Leads with no activity in the last N days." */
  days_since_contact?: number;
  /** Migration 0178 — transaction id of the property the lead came from. */
  property?: number;
}

export function listLeads(filters: ListLeadsFilters = {}): Promise<{ leads: LeadListItem[] }> {
  const params = new URLSearchParams();
  if (filters.stage) params.set('stage', filters.stage);
  if (filters.assignee !== undefined && filters.assignee !== 'all') {
    params.set('assignee', String(filters.assignee));
  }
  if (filters.source) params.set('source', filters.source);
  if (filters.q) params.set('q', filters.q);
  if (filters.tag_ids?.length) params.set('tag_ids', filters.tag_ids.join(','));
  if (filters.lead_type) params.set('lead_type', filters.lead_type);
  if (filters.days_since_contact != null && filters.days_since_contact > 0) {
    params.set('days_since_contact', String(filters.days_since_contact));
  }
  if (filters.property != null && filters.property > 0) {
    params.set('property', String(filters.property));
  }
  const qs = params.toString();
  return apiFetch(`/api/leads${qs ? `?${qs}` : ''}`);
}

// ─── The future pipeline (migration 0182, Daniel 2026-08-05) ──────────

/**
 * One card on the pipeline board.
 *
 * `bucket` is computed by the SERVER against the server's date, not by the
 * browser. The two would agree almost always, and "almost" is the problem: a
 * phone left open overnight would draw a board bucketed against yesterday, and
 * the whole point of deriving the bucket at read time is that it is right today.
 */
export interface PipelineLead {
  id: number;
  contact_id: number;
  stage: LeadStage;
  source: string | null;
  source_detail: string | null;
  assigned_agent_id: number | null;
  next_action_at: number | null;
  lead_type: LeadType | null;
  notes: string;
  target_month: number | null;
  target_year: number | null;
  target_confidence: LeadConfidence | null;
  intent: LeadIntent | null;
  created_at: number;
  last_activity_at: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  assignee_first: string | null;
  assignee_last: string | null;
  /** Open follow-up tasks on this lead, and how many are already past due. */
  open_follow_ups: number;
  overdue_follow_ups: number;
  /** Which column, decided server-side for today. */
  bucket: PipelineBucket;
  /** "March 2027", or null when no timeline is set (ASAP). */
  target_label: string | null;
  /** Smart plans this person is running now (active or paused). */
  plans: { plan_id: number; plan_name: string; status: string }[];
  /** The plan picked for this lead's column, if one is set. */
  suggested_plan_id: number | null;
  on_suggested_plan: boolean;
}

export type BucketPlans = Record<PipelineBucket, { plan_id: number; name: string } | null>;

export interface PipelineResponse {
  leads: PipelineLead[];
  counts: Partial<Record<PipelineBucket, number>>;
  bucket_plans: BucketPlans;
  /** The day the server bucketed against. Month is 1-12. */
  today: { year: number; month: number };
}

export function listPipeline(filters: {
  assignee?: 'me' | 'unassigned' | number;
  intent?: LeadIntent | '';
} = {}): Promise<PipelineResponse> {
  const params = new URLSearchParams();
  if (filters.assignee !== undefined) params.set('assignee', String(filters.assignee));
  if (filters.intent) params.set('intent', filters.intent);
  const qs = params.toString();
  return apiFetch(`/api/leads/_meta/pipeline${qs ? `?${qs}` : ''}`);
}

/** The smart plan picked for each pipeline column. */
export function getPipelinePlans(): Promise<{ bucket_plans: BucketPlans }> {
  return apiFetch('/api/leads/_meta/pipeline-plans');
}

/** Owner / operations / admin. Pass null to clear a column. */
export function setPipelinePlans(
  plans: Partial<Record<PipelineBucket, number | null>>,
): Promise<{ ok: true; bucket_plans: BucketPlans }> {
  return apiFetch('/api/leads/_meta/pipeline-plans', {
    method: 'PUT',
    body: JSON.stringify({ plans }),
  });
}

/** Start the smart plan for the column this lead is in today. */
export function startPipelinePlan(
  leadId: number,
): Promise<{ ok: true; already: boolean; plan_name: string; cancelled?: number }> {
  return apiFetch(`/api/leads/${leadId}/pipeline-plan`, { method: 'POST', body: '{}' });
}

/**
 * Put a contact on the future pipeline.
 *
 * Idempotent per person: if they already have an open lead it is updated in
 * place, and `reused` comes back true. Calling this twice never produces two
 * leads for one contact, which is the difference between it and `createLead`.
 */
export function addToPipeline(body: {
  contact_id: number;
  intent?: LeadIntent | null;
  target_month?: number | null;
  target_year?: number | null;
  target_confidence?: LeadConfidence | null;
}): Promise<{ ok: true; id: number; created: boolean; reused: boolean }> {
  return apiFetch('/api/leads/_meta/pipeline', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * A follow-up on a lead. This is a real `tasks` row, not the old bare
 * `leads.next_action_at` timestamp — it shows up on the team task list, it can
 * be completed, and it fires a bell when it comes due.
 */
export interface LeadFollowUp {
  id: number;
  title: string;
  description: string | null;
  due_date: number | null;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  assignee_user_id: number | null;
  assignee_first: string | null;
  assignee_last: string | null;
  completed_at: number | null;
  created_at: number;
}

export function listLeadFollowUps(leadId: number): Promise<{ follow_ups: LeadFollowUp[] }> {
  return apiFetch(`/api/leads/${leadId}/follow-ups`);
}

export function createLeadFollowUp(
  leadId: number,
  body: { due_date: number; title?: string; description?: string; assignee_user_id?: number | null },
): Promise<{ ok: true; id: number }> {
  return apiFetch(`/api/leads/${leadId}/follow-ups`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Build 11 (2026-09-14): "done" can carry what happened and when to check
 * again. All optional; the bare call still just completes the task.
 */
export function completeLeadFollowUp(
  leadId: number,
  taskId: number,
  extras: { loan_status?: LeadLoanStatus; note?: string; next_due_date?: number | null } = {},
): Promise<{ ok: true; next_task_id: number | null }> {
  return apiFetch(`/api/leads/${leadId}/follow-ups/${taskId}/complete`, {
    method: 'POST',
    body: JSON.stringify(extras),
  });
}

/** Build 11: the quick-add form and the paste-a-list import. */
export interface QuickLeadRow {
  first_name: string;
  last_name?: string;
  phone?: string;
  email?: string;
  source: string;
  lender_contact_id?: number;
  lender_name?: string;
  lender_phone?: string;
  lender_email?: string;
  situation?: string;
  follow_up_in_days?: number;
  lender_confirmed?: boolean;
  assigned_agent_id?: number;
  /** Build 16: the listing or auction the lead asked about. */
  property_transaction_id?: number;
}
export function quickAddLeads(rows: QuickLeadRow[]): Promise<{
  ok: true;
  created: Array<{ row: number; lead_id: number; contact_id: number; was_existing: boolean; lender_contact_id: number | null; task_id: number | null }>;
  failed: Array<{ row: number; error: string }>;
}> {
  return apiFetch('/api/leads/quick', { method: 'POST', body: JSON.stringify({ rows }) });
}

// ─── Property attribution (migration 0178, Daniel 2026-08-04) ─────────

export interface LeadProperty {
  id: number;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  type: 'listing' | 'auction' | 'buyer';
  /** Only on /_meta/properties — how many of the caller's leads it produced. */
  lead_count?: number;
  /** Only on /_meta/property-options. */
  status?: string;
}

/** One-line address for a property, for dropdowns and cards. */
export function propertyLabel(p: {
  address_street: string | null;
  address_city: string | null;
  address_state?: string | null;
  id: number;
}): string {
  const line = [p.address_street, p.address_city, p.address_state ?? null]
    .filter(Boolean)
    .join(', ');
  return line || `Listing #${p.id}`;
}

/** Options for the property filter — listings that actually produced leads the
 *  caller can see, so the dropdown is short and every option returns rows. */
export function listLeadProperties(): Promise<{ properties: LeadProperty[] }> {
  return apiFetch('/api/leads/_meta/properties');
}

/** Every listing on the team, for the pickers. Includes listings with no leads
 *  yet, which is the normal case for a property that just went live. */
export function listLeadPropertyOptions(): Promise<{ properties: LeadProperty[] }> {
  return apiFetch('/api/leads/_meta/property-options');
}

export interface LeadCoAgent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export function getLead(id: number): Promise<{
  lead: LeadDetail;
  history: LeadStageHistoryEntry[];
  co_agents: LeadCoAgent[];
}> {
  return apiFetch(`/api/leads/${id}`);
}

export function addLeadCoAgent(leadId: number, userId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/leads/${leadId}/co-agents`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export function removeLeadCoAgent(leadId: number, userId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/leads/${leadId}/co-agents/${userId}`, { method: 'DELETE' });
}

export interface CreateLeadBody {
  contact_id?: number;
  new_contact?: { first_name: string; last_name?: string; email?: string; phone?: string; company?: string };
  source?: string;
  source_detail?: string;
  stage?: LeadStage;
  assigned_agent_id?: number | null;
  next_action_at?: number | null;
  lender_contact_id?: number | null;
  lead_type?: LeadType | null;
  /** Migration 0146 — buyer leads only. */
  buyer_type?: BuyerType | null;
  notes?: string;
  /** Migration 0182 — send the month and the year together or not at all. */
  target_month?: number | null;
  target_year?: number | null;
  target_confidence?: LeadConfidence | null;
  intent?: LeadIntent | null;
}

export function createLead(body: CreateLeadBody): Promise<{ ok: true; id: number }> {
  return apiFetch('/api/leads', { method: 'POST', body: JSON.stringify(body) });
}

export interface UpdateLeadBody {
  source?: string | null;
  source_detail?: string | null;
  stage?: LeadStage;
  stage_notes?: string;
  /** Build 71: required by the server when stage moves to lost. */
  lost_reason?: LostReason;
  lost_note?: string;
  assigned_agent_id?: number | null;
  next_action_at?: number | null;
  score?: number;
  lender_contact_id?: number | null;
  lead_type?: LeadType | null;
  /** Migration 0146 — 'traditional' | 'investor' | null (clears it). */
  buyer_type?: BuyerType | null;
  notes?: string;
  /** Migration 0178 — transaction id of the property, or null to clear it. */
  property_transaction_id?: number | null;
  // Round L-7 — pre-approval pipeline fields.
  pre_approval_amount_cents?: number | null;
  budget_min_cents?: number | null;
  budget_max_cents?: number | null;
  /**
   * Migration 0182. The month and year are patched as a PAIR — send both to set
   * a timeline, or both as null to clear it. Sending one alone is a 400 rather
   * than a half-stored timeline the board would read as "not set" while the form
   * still showed a value.
   */
  target_month?: number | null;
  target_year?: number | null;
  target_confidence?: LeadConfidence | null;
  intent?: LeadIntent | null;
  /** Build 11: handoff confirmed (starts automatic lender check-ins). */
  lender_confirmed?: boolean;
  /** Build 11: this lead's check-in cadence in days; null = team default (2). */
  follow_up_every_days?: number | null;
}

/**
 * `auto_sync` says what the server changed on its own to keep stage and
 * timeline in step (Daniel 2026-10-26), so the screen can tell the agent.
 */
export function updateLead(
  id: number,
  patch: UpdateLeadBody,
): Promise<{ ok: true; auto_sync?: { stage?: LeadStage; timeline_cleared?: boolean } }> {
  return apiFetch(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Build 19 (Daniel 2026-09-16): link many leads to one property. null clears. */
export function bulkSetLeadProperty(
  leadIds: number[],
  propertyTransactionId: number | null,
): Promise<{ ok: true; updated: number; skipped: number }> {
  return apiFetch('/api/leads/bulk/property', {
    method: 'POST',
    body: JSON.stringify({ lead_ids: leadIds, property_transaction_id: propertyTransactionId }),
  });
}

// ─── Round L-7: pre-approval pipeline ──────────────────────────────────
export type LeadLoanStatus =
  | 'made_contact'
  // Build 11 (2026-09-14): where lender follow-ups actually live.
  | 'docs_requested'
  | 'docs_received'
  | 'application_received'
  | 'pre_approval_issued'
  | 'loan_submitted'
  | 'conditional_approval'
  | 'cleared_to_close'
  | 'funded'
  // L-112 (Daniel 2026-07-29): denied outcomes for the kanban.
  | 'denied_needs_work'
  | 'denied_dead';

export const LEAD_LOAN_STATUS_LABELS: Record<LeadLoanStatus, string> = {
  made_contact: 'Made contact',
  docs_requested: 'Docs requested',
  docs_received: 'Docs received',
  application_received: 'Application received',
  pre_approval_issued: 'Pre-approval issued',
  loan_submitted: 'Loan submitted',
  conditional_approval: 'Conditional approval',
  cleared_to_close: 'Cleared to close',
  funded: 'Funded',
  denied_needs_work: 'Denied — needs work',
  denied_dead: 'Denied — dead',
};

export interface LeadLoanStatusUpdate {
  id: number;
  status: LeadLoanStatus;
  notes: string | null;
  created_at: number;
  created_by_portal: 'agent' | 'partner';
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export function getLeadLoanStatus(leadId: number): Promise<{
  updates: LeadLoanStatusUpdate[];
  current_status: LeadLoanStatus | null;
  current_status_at: number | null;
}> {
  return apiFetch(`/api/leads/${leadId}/loan-status`);
}

export function postLeadLoanStatus(
  leadId: number,
  body: { status: LeadLoanStatus; notes?: string },
): Promise<{ ok: true; id: number; created_at: number }> {
  return apiFetch(`/api/leads/${leadId}/loan-status`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// L-112 (Daniel 2026-07-29): agent-side "will the lender see this
// lead in their portal?" diagnostic. Renders as a chip on the lead's
// lender card so visibility problems self-diagnose.
export interface LenderVisibility {
  visible: boolean;
  reason: 'ok' | 'no_lender' | 'contact_deleted' | 'no_portal_account' | 'predicate_miss' | 'stage_filtered';
  detail: string;
}

export function getLenderVisibility(leadId: number): Promise<LenderVisibility> {
  return apiFetch(`/api/leads/${leadId}/lender-visibility`);
}

export interface LeadChatMessage {
  id: number;
  body: string;
  created_at: number;
  read_at: number | null;
  sender_portal: 'agent' | 'partner';
  first_name: string | null;
  last_name: string | null;
}

export function getLeadLenderChat(leadId: number): Promise<{
  messages: LeadChatMessage[];
  unread_count: number;
}> {
  return apiFetch(`/api/leads/${leadId}/lender-chat`);
}

export function postLeadLenderChat(
  leadId: number,
  body: string,
): Promise<{ ok: true; id: number; created_at: number }> {
  return apiFetch(`/api/leads/${leadId}/lender-chat`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export interface LeadDocument {
  id: number;
  name: string;
  description: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  document_type: string | null;
  created_at: number;
  uploader_first: string | null;
  uploader_last: string | null;
}

export function listLeadDocuments(leadId: number): Promise<{ documents: LeadDocument[] }> {
  return apiFetch(`/api/leads/${leadId}/documents`);
}

export function deleteLeadDocument(leadId: number, docId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/leads/${leadId}/documents/${docId}`, { method: 'DELETE' });
}

/**
 * Forecasted pipeline volume (Round L-7). Returns the per-stage breakdown
 * and aggregate dollars. Hidden until the team has at least one lead with
 * a pre_approval_amount set, otherwise the number is misleading.
 */
export interface LeadForecast {
  forecast_cents: number;
  by_stage: Array<{
    stage: string;
    lead_count: number;
    gross_cents: number;
    probability: number;
    forecast_cents: number;
  }>;
  probabilities: Record<string, number>;
}

export function getLeadForecast(): Promise<LeadForecast> {
  return apiFetch('/api/leads/_meta/forecast');
}

/** Lender-portal view — leads where the caller is the assigned lender. */
export interface LenderLeadListItem {
  id: number;
  stage: LeadStage;
  pre_approval_amount_cents: number | null;
  budget_min_cents: number | null;
  budget_max_cents: number | null;
  created_at: number;
  next_action_at: number | null;
  buyer_first: string | null;
  buyer_last: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  // L-114 (Daniel 2026-07-29): the referring agent, so the lender's
  // board can keep each agent's deals separate.
  agent_user_id: number | null;
  agent_first: string | null;
  agent_last: string | null;
  latest_status: LeadLoanStatus | null;
  latest_status_at: number | null;
  unread_count: number;
}

export function listLenderLeads(): Promise<{ leads: LenderLeadListItem[] }> {
  return apiFetch('/api/leads/_meta/lender-leads');
}

export function deleteLead(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/leads/${id}`, { method: 'DELETE' });
}

export interface ConvertLeadBody {
  type: 'listing' | 'buyer' | 'auction';
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  list_price?: number | null;
  sale_price?: number | null;
  contract_date?: number | null;
  close_date?: number | null;
  mls_number?: string;
  notes?: string;
}

export function convertLead(id: number, body: ConvertLeadBody): Promise<{ ok: true; transaction_id: number }> {
  return apiFetch(`/api/leads/${id}/convert`, { method: 'POST', body: JSON.stringify(body) });
}

/** Build 100: the grouped list lives in lib/lead-sources.ts now. Kept as the
 *  flat list for anything that still imports it. */
export const SOURCE_PRESETS: string[] = LEAD_SOURCES;
